import React from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Drawer } from 'vaul';
import { QrCode, CreditCard, Keyboard, ChevronLeft, ClipboardPaste } from 'lucide-react';
import toast from 'react-hot-toast';
import { addVPA } from '../../redux/vpaActions';
import { useCollectionMode } from '../../hooks/useCollectionMode';
import { normalizeWalletAddress } from '../../utils/collectionMode';

const vpaSchema = z.object({
  vpa: z.string().min(1, 'VPA is required').regex(/^.+\@.+$/, 'VPA must be in format id@bank'),
  alias: z.string().optional(),
});

const walletSchema = z.object({
  vpa: z
    .string()
    .min(1, 'Wallet address is required')
    .refine((s) => !!normalizeWalletAddress(s), 'Enter a valid wallet address'),
  alias: z.string().optional(),
});

type FormValues = z.infer<typeof vpaSchema>;

interface AddVpaDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: number | string;
  tenantName?: string;
  onSuccess?: () => void;
}

interface AddVpaDrawerImplProps extends AddVpaDrawerProps {
  isWalletAddressCollection: boolean;
}

function AddVpaDrawerImpl({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  onSuccess,
  isWalletAddressCollection,
}: AddVpaDrawerImplProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const schema = isWalletAddressCollection ? walletSchema : vpaSchema;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { vpa: '', alias: '' },
    mode: 'onChange',
  });

  const [justAdded, setJustAdded] = React.useState(false);
  const [manualMode, setManualMode] = React.useState(false);
  const [isPasting, setIsPasting] = React.useState(false);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      if (isWalletAddressCollection) {
        const addr = normalizeWalletAddress(data.vpa);
        if (!addr) return;
        await dispatch(
          // @ts-expect-error thunk action type
          addVPA(
            null,
            addr,
            'BLOCKCHAIN',
            'ACTIVE',
            data.alias || null,
            tenantId,
            undefined,
            undefined,
            'wallet',
          ),
        );
      } else {
        const trimmed = data.vpa.trim().toLowerCase();
        const providerName = trimmed.includes('@') ? trimmed.split('@')[1] : null;
        await dispatch(
          // @ts-expect-error thunk action type
          addVPA(null, trimmed, providerName, 'ACTIVE', data.alias || null, tenantId),
        );
      }
      setJustAdded(true);
    } catch {
      // Error toast from vpaActions
    }
  };

  const handleAddAnother = () => {
    setJustAdded(false);
    reset({ vpa: '', alias: '' });
    setTimeout(() => {
      const el = document.getElementById('add-vpa-input');
      if (el instanceof HTMLInputElement) el.focus();
    }, 100);
  };

  const handleDone = () => {
    setJustAdded(false);
    setManualMode(false);
    reset();
    onOpenChange(false);
    onSuccess?.();
  };

  const handleScanQr = () => {
    onOpenChange(false);
    navigate(`/tenant/vpa/add/${tenantId}`, { state: { returnTo: 'app' } });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setJustAdded(false);
      setManualMode(false);
      reset();
    }
    onOpenChange(next);
  };

  const handlePasteVpa = async () => {
    try {
      setIsPasting(true);
      const text = await navigator.clipboard.readText();
      if (!text) return;
      const v = isWalletAddressCollection ? text.trim() : text.trim().toLowerCase();
      setValue('vpa', v, { shouldValidate: true, shouldDirty: true });
      toast.success('Pasted');
    } catch {
      toast.error('Unable to paste');
    } finally {
      setIsPasting(false);
    }
  };

  const title = isWalletAddressCollection ? 'Add wallet address' : 'Add VPA';
  const successLine = isWalletAddressCollection
    ? 'Wallet address added successfully'
    : 'VPA Added Successfully';

  return (
    <Drawer.Root open={open} onOpenChange={handleOpenChange} direction="bottom">
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Drawer.Content className="fixed inset-x-0 bottom-0 z-[60] flex justify-center">
          <div
            className="w-full max-w-2xl flex flex-col bg-white rounded-t-2xl border border-gray-200 shadow-soft-lg max-h-[88vh]"
            style={{ paddingBottom: 'var(--safe-area-inset-bottom)' }}
          >
            <Drawer.Title className="sr-only">{title}</Drawer.Title>
            <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-gray-300 mt-3" />
            <div className="flex-1 overflow-auto p-4 pb-5 drawer-safe-bottom">
              <h2 className="flex items-center gap-2 text-base sm:text-lg font-semibold text-gray-900 mb-1">
                <CreditCard className="w-5 h-5 text-brand" />
                {title}
              </h2>
              {tenantName && (
                <p className="text-sm text-gray-500 mb-4">{tenantName}</p>
              )}

              {justAdded ? (
                <div className="space-y-4 py-4">
                  <p className="text-success font-medium">{successLine}</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleAddAnother}
                      className="flex-1 h-[48px] px-4 border border-gray-200 rounded-md font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Add Another
                    </button>
                    <button
                      type="button"
                      onClick={handleDone}
                      className="flex-1 h-[44px] px-4 fc-btn-primary"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : !manualMode ? (
                <div className="space-y-4">
                  <div className="fc-card p-4">
                    <p className="text-xs uppercase tracking-[0.08em] font-semibold text-slate-500 mb-1">
                      Recommended
                    </p>
                    <p className="text-sm text-slate-700">
                      {isWalletAddressCollection
                        ? 'Scan a QR code that contains the wallet address for fastest capture.'
                        : 'Scan merchant QR for fastest and accurate VPA capture.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleScanQr}
                    className="w-full min-h-[50px] px-4 fc-btn-primary"
                  >
                    <QrCode className="w-5 h-5" />
                    Scan QR (Fastest)
                  </button>
                  <button
                    type="button"
                    onClick={() => setManualMode(true)}
                    className="w-full flex items-center justify-center gap-2 min-h-[48px] px-4 border border-slate-200 rounded-md font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                  >
                    <Keyboard className="w-5 h-5" />
                    {isWalletAddressCollection ? 'Enter wallet address manually' : 'Enter VPA manually'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <button
                    type="button"
                    onClick={() => setManualMode(false)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isWalletAddressCollection ? 'Wallet address' : 'VPA ID'}
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="add-vpa-input"
                        {...register('vpa', {
                          setValueAs: (value) =>
                            typeof value === 'string'
                              ? isWalletAddressCollection
                                ? value.trim()
                                : value.trim().toLowerCase()
                              : value,
                        })}
                        placeholder={isWalletAddressCollection ? '0x… or chain address' : 'upi_id@bank'}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        className="fc-input px-4 py-3"
                      />
                      <button
                        type="button"
                        onClick={handlePasteVpa}
                        disabled={isPasting}
                        className="inline-flex items-center justify-center min-w-[44px] px-3 border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50"
                        aria-label={isWalletAddressCollection ? 'Paste wallet address' : 'Paste VPA'}
                      >
                        <ClipboardPaste className="w-4 h-4" />
                      </button>
                    </div>
                    {errors.vpa && (
                      <p className="mt-1 text-sm text-danger">{errors.vpa.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isWalletAddressCollection ? 'Label (optional)' : 'Alias (optional)'}
                    </label>
                    <input
                      {...register('alias')}
                      placeholder={isWalletAddressCollection ? 'Optional name' : 'Merchant name'}
                      className="fc-input px-4 py-3"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleScanQr}
                    className="w-full flex items-center justify-center gap-2 min-h-[48px] px-4 border border-slate-200 rounded-md font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100"
                  >
                    <QrCode className="w-5 h-5" />
                    Scan QR
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting || !isValid}
                    className="w-full min-h-[48px] px-4 py-2.5 fc-btn-primary text-sm"
                  >
                    {isSubmitting
                      ? 'Saving...'
                      : isWalletAddressCollection
                        ? 'Save wallet address'
                        : 'Save VPA'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function AddVpaDrawer(props: AddVpaDrawerProps) {
  const { isWalletAddressCollection } = useCollectionMode();
  return (
    <AddVpaDrawerImpl
      key={isWalletAddressCollection ? 'wallet' : 'vpa'}
      {...props}
      isWalletAddressCollection={isWalletAddressCollection}
    />
  );
}

export default AddVpaDrawer;
